/**
 * Pro-Rata Solid Weight Calculation
 *
 * Pro-rata collections compute their payable amount with an adjusted final rate
 * (milk_rate + fat step-up + SNF step-down). That adjusted amount must NOT feed the
 * solid weight: solid weight is always the STANDARD-collection equivalent, i.e.
 * (fat_kg * fat_rate + snf_kg * snf_rate) / milk_rate, exactly like
 * calculateDerivedValuesStandard in ./milkCalculations.
 *
 * This module intentionally ignores rate charts — the value is independent of
 * pro-rata rate adjustments by design.
 *
 * It also intentionally ignores the collection's base SNF: solid weight is always
 * computed on a fixed 9.0 SNF basis, whatever the dairy is configured with and
 * whatever per-row override the user typed. ONLY solid weight is pinned this way —
 * the pro-rata amount and the displayed snf_rate still use the row's real base SNF,
 * and standard (non pro-rata) collections are unaffected.
 */

import {
  calculateFatKg,
  calculateSnfKg,
  calculateFatRate,
  calculateSnfRate,
  calculateAmountMobileAPKStyle
} from './milkCalculations';

/** Fixed SNF basis for solid weight. Deliberately not configurable — see module header. */
const SOLID_WEIGHT_BASE_SNF = 9.0;

/**
 * @param {Object} collection - Collection fields (aliases supported):
 *   weight|kg, fat_percent|fat_percentage, snf_percent|snf_percentage,
 *   milk_rate, fat_snf_ratio ('60/40' or '60_40').
 *   base_snf / base_snf_percentage are NOT read — the 9.0 basis is fixed.
 *   In CLR mode, pass the already CLR-resolved SNF value (existing caller behavior).
 * @param {Object} [options]
 * @param {string} [options.defaultFatSnfRatio='60/40']
 * @param {number|string} [options.fallbackMilkRate=0] - used when collection.milk_rate is missing/0
 * @returns {string} Solid weight in kg, formatted to 3 decimals ('0.000' when weight or rate is 0)
 */
export const calculateProRataSolidWeight = (collection = {}, options = {}) => {
  const {
    defaultFatSnfRatio = '60/40',
    fallbackMilkRate = 0
  } = options;

  const weight = parseFloat(collection.weight ?? collection.kg ?? 0) || 0;
  const fat = parseFloat(collection.fat_percent ?? collection.fat_percentage ?? 0) || 0;
  const snf = parseFloat(collection.snf_percent ?? collection.snf_percentage ?? 0) || 0;
  const milkRate = parseFloat(collection.milk_rate ?? 0) || parseFloat(fallbackMilkRate) || 0;

  if (weight <= 0 || milkRate <= 0) return '0.000';

  const fatSnfRatio = String(collection.fat_snf_ratio || defaultFatSnfRatio).replace('_', '/');

  const fatKg = calculateFatKg(weight, fat);
  const snfKg = calculateSnfKg(weight, snf);
  const fatRate = calculateFatRate(milkRate, fatSnfRatio);
  const snfRate = calculateSnfRate(milkRate, fatSnfRatio, SOLID_WEIGHT_BASE_SNF);

  const standardAmount = calculateAmountMobileAPKStyle(fatKg, snfKg, fatRate, snfRate);
  return (standardAmount / milkRate).toFixed(3);
};
